// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Loader from "src/components/shared/loaders/Loader/Loader";
import ProgressionTrendlinesChart from "./ProgressionTrendlinesChart/ProgressionTrendlinesChart";
import ProgressionTrendlinesLegend from "./ProgressionTrendlinesLegend/ProgressionTrendlinesLegend";
import ProgressionLineCharts from "../ProgressionLineCharts/ProgressionLineCharts";

// Types
import {
  ProgressionTrendlineVisitType,
  ProgressionTrendlineType,
} from "src/services/exams/types";

type ProgressionTrendlinesType = {
  visits: Array<ProgressionTrendlineVisitType>;
  eye: string;
  isLoading: boolean;
  chartData: Array<{
    age: string | number;
    time: string;
    MD: string | number | null;
    PSD: string | number | null;
    VRFI: string | number | null;
    pointShape: string;
    isRetinaLogikExam: boolean;
  }>;
  chartHeight?: number;
  displayHorizontally?: boolean;
};

type MetricType = keyof ProgressionTrendlineVisitType["trendlines"];

// Charted in this order; each y-axis covers at least the given range
const METRIC_RANGES: Array<{ metric: MetricType; yRange: [number, number] }> = [
  { metric: "MD", yRange: [-35, 5] },
  { metric: "PSD", yRange: [0, 20] },
  { metric: "VRFI", yRange: [0, 100] },
];

const ProgressionTrendlines = ({
  visits,
  eye,
  isLoading,
  chartData,
  chartHeight = 300,
  displayHorizontally = false,
}: ProgressionTrendlinesType) => {
  const { t } = useTranslation();

  // Each visit's trendlines are fitted over every exam up to it, so the
  // latest visit has the full picture
  const charts = useMemo(() => {
    const latestVisit = [...visits].sort(
      (a, b) => a.visit_index - b.visit_index
    )[visits.length - 1];

    return METRIC_RANGES.flatMap(({ metric, yRange }) => {
      const trendline: ProgressionTrendlineType | undefined =
        latestVisit?.trendlines[metric];
      return trendline ? [{ metric, yRange, trendline }] : [];
    });
  }, [visits]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "fit-content",
        gap: "20px",
      }}
    >
      <Typography fontWeight={600}>{t("Progression Charts")}</Typography>

      {isLoading ? (
        <Loader
          containerProps={{
            my: "20px",
            width: "100px",
            fontSize: "14px",
            gap: "16px",
          }}
          text={t("Loading...")}
        />
      ) : visits?.length > 0 ? (
        <>
          <ProgressionTrendlinesLegend />
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: displayHorizontally ? "row" : "column",
              rowGap: "20px",
              columnGap: "0px",
            }}
          >
            {charts.map(({ metric, yRange, trendline }) => (
              <ProgressionTrendlinesChart
                key={metric}
                trendline={trendline}
                eye={eye}
                yRange={yRange}
                chartHeight={chartHeight}
              />
            ))}
          </div>
        </>
      ) : (
        <ProgressionLineCharts
          chartData={chartData}
          displayHorizontally={displayHorizontally}
        />
      )}
    </Box>
  );
};

export default ProgressionTrendlines;
